import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Trans, useLingui } from '@lingui/react/macro'

import * as Form from '@/features/form'
import Checkbox from '@/components/ui/Checkbox'
import { MultipleRecurrenceSelect } from '@/features/recurrence'
import PriceInput from '@/components/ui/PriceInput'
import Input from '@/components/ui/Input'
import DurationInput from '@/components/ui/DurationInput'
import { Seperator } from '@/components/ui/Seperator'
import { FormData } from '../../hooks/useGoodieForm'
import Dropdown from '@/components/ui/Dropdown'
import { useDefaultButtonTypeOptions } from '../../utils/helpers'
import { FileUpload } from '@/features/files'
import { RequestFieldsInput } from '../RequestFieldsInput'

export function initFields(goodie?: GraphQL.GoodieFragmentFragment) {
  if (!goodie) {
    return {
      bookWithSuggestions: false,
      location: '',
      openingHours: [],
      button: GraphQL.DefaultButtonType.Hide,
      requestFields: []
    }
  }

  switch (goodie?.__typename) {
    case 'Default': {
      const newDefault = { ...goodie.default }
      delete newDefault.__typename

      return {
        ...newDefault,
        openingHours: goodie.default.openingHours.map(rec => {
          const recurrence = {
            ...getFragment(SharedAPI.recurrence.RECURRENCE_FRAGMENT, rec)
          }
          delete recurrence.__typename

          return recurrence
        })
      }
    }
    default:
      return null
  }
}

export default function DefaultFields() {
  const { t } = useLingui()

  const buttonOptions = useDefaultButtonTypeOptions()

  return (
    <div className="flex flex-col gap-6">
      <div className="flex max-w-full flex-row flex-wrap items-start justify-evenly gap-6">
        <Form.Field
          name="default.location"
          label={t`Location`}
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              placeholder={t({
                comment: 'Placeholder for the Location Input',
                message: 'e.g.: Spa-Reception'
              })}
              {...props}
            />
          )}
        </Form.Field>
        <Form.Field
          name="default.price"
          label={`${t`Price (0 when free)`}*`}
          rules={{
            validate: {
              required(price) {
                if (price == null) return t`This field is required`
              }
            }
          }}
        >
          {({ renderProps }) => (
            <PriceInput
              placeholder={t({
                comment: 'Placeholder for the Price Input',
                message: 'e.g.: 100'
              })}
              {...renderProps}
            />
          )}
        </Form.Field>
      </div>
      <Form.Data<FormData>>
        {({ default: { duration } }, { setValue, readOnly }) => {
          return (
            <Form.Field
              name="default.duration"
              label={t`Duration`}
              canBeDisabled={!readOnly}
              isInitialEnabled={duration != null}
              onEnabledChange={isEnabled => {
                if (!isEnabled) setValue('default.duration', null)
              }}
            >
              {({ renderProps }) => <DurationInput {...renderProps} />}
            </Form.Field>
          )
        }}
      </Form.Data>
      <Form.Data<FormData>>
        {({ default: { openingHours } }, { readOnly }) => {
          return (
            <Form.Field
              name="default.openingHours"
              label={t`Opening Times`}
              canBeDisabled={!readOnly && openingHours.length === 0}
              isInitialEnabled={openingHours.length > 0}
            >
              {({ renderProps: { value, ...props } }) => (
                <MultipleRecurrenceSelect
                  recurrences={value}
                  hasDuration
                  {...props}
                />
              )}
            </Form.Field>
          )
        }}
      </Form.Data>
      <Seperator heading={t`Request options`} />
      <Form.Field<FormData>
        name="default.button"
        label={t`Button`}
        description={t`What should happen when a guest clicks on the request button.`}
      >
        {({ renderProps: props }) => (
          <Dropdown options={buttonOptions} {...props} />
        )}
      </Form.Field>
      <Form.Data<FormData>>
        {({ default: { button } }) => {
          switch (button) {
            case GraphQL.DefaultButtonType.Bookable:
              return <BookableField />

            case GraphQL.DefaultButtonType.File:
              return <FileField />

            case GraphQL.DefaultButtonType.Link:
              return <LinkField />

            case GraphQL.DefaultButtonType.Hide:
              return null
          }
        }}
      </Form.Data>
    </div>
  )
}

function BookableField() {
  return (
    <>
      <Form.Field name="default.bookWithSuggestions" resetOnUnmount>
        {({ renderProps: { value, onChange, ...props } }) => (
          <Checkbox
            checked={value}
            onChange={() => onChange(!value)}
            {...props}
          >
            <Trans comment="Checkbox label, where hotels can enable/disable if the guests need to send date ranges of their availability for this default.">
              Guests have to send Date ranges when they are available
            </Trans>
          </Checkbox>
        )}
      </Form.Field>
      <RequestFieldsInput name="default.requestFields" />
    </>
  )
}

function FileField() {
  const { t } = useLingui()

  return (
    <Form.Field name="default.buttonFile" resetOnUnmount>
      {({ locale, isDefaultLocale, renderProps }) => (
        <FileUpload
          locale={locale}
          isDefaultLocale={isDefaultLocale}
          placeholder={t`Upload File`}
          {...renderProps}
          className="w-64 aspect-square"
        />
      )}
    </Form.Field>
  )
}

function LinkField() {
  const { t } = useLingui()

  return (
    <Form.Field
      resetOnUnmount
      name="default.buttonLink"
      label={t`Button Link`}
      rules={{
        maxLength: {
          value: 255,
          message: t`Can't be longer than 255 characters.`
        },
        validate: {
          required: Form.Validations.requiredWithTrimValidation(
            t`This field is required`
          )
        }
      }}
    >
      {({ renderProps: { onChange, ...props } }) => (
        <Input
          onChange={event => onChange(event.target.value)}
          type="url"
          placeholder={t({
            comment: 'Placeholder for a URL Input',
            message: 'e.g.: https://guestgoodie.com'
          })}
          {...props}
        />
      )}
    </Form.Field>
  )
}
